import { useEffect, useMemo, useRef, useState } from 'react';

import type { PriorityPlaylist } from '@backrs_ui/common-ui';

import {
  useGetTasksForPlaylistQuery,
  useGetTasksForSuggestedPlaylistQuery,
  useUpdateTaskOrderMutation,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import { PriorityTask } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Skeleton, SxProps, Theme } from '@mui/material';

import { PlaybookTaskRow } from './PlaybookTaskRow';
import { TaskCard } from './TaskCard';
import {
  PLAYLIST_CARD_DRAWER_WIDTH,
  PLAYLIST_SKELETON_HEIGHT,
} from '../constants/priorities.constants';
import { usePlaylistTasksSort } from '../hooks/playlists.hooks';
import { useGetTaskActionsAndProperties } from '../hooks/tasks.hooks';
import { usePlaylistDnd } from '../hooks/usePlaylistDnd';
import { getToggledTaskStatusCode } from '../utils/tasks.utils';

function mergeServerIntoLocalPreservingOrder(
  local: PriorityTask[] | null | undefined,
  server: PriorityTask[] | null | undefined,
): PriorityTask[] | null {
  if (!server) {
    return local ?? null;
  }
  if (!local || local.length === 0) {
    return server;
  }

  const serverMap = new Map<number, PriorityTask>(server.map((t) => [t.id, t]));
  // keep local order, but merge in server fields if present
  const merged = local.map((t) => {
    const s = serverMap.get(t.id);

    return s ? { ...t, ...s } : t;
  });

  return merged;
}

export const usePlaylistTaskList = (playlistId: number | null | undefined, isTemplate = false) => {
  const [localTasks, setLocalTasks] = useState<PriorityTask[] | null>(null);
  const [preferLocalOrder, setPreferLocalOrder] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [draggingFlag, setDraggingFlag] = useState(false);

  const [updateTaskOrderRaw] = useUpdateTaskOrderMutation();
  const updateTaskOrder = (args: { playlistId: number; taskIds: number[] }) =>
    updateTaskOrderRaw(args).unwrap();

  // A template's tasks belong to its author, so the owner-scoped endpoint returns
  // nothing for anyone else; the suggested endpoint filters by the template instead.
  const { data: ownTasks, isLoading: isLoadingOwnTasks } = useGetTasksForPlaylistQuery(
    playlistId || 0,
    { skip: !playlistId || isTemplate },
  );
  const { data: templateTasks, isLoading: isLoadingTemplateTasks } =
    useGetTasksForSuggestedPlaylistQuery(playlistId || 0, { skip: !playlistId || !isTemplate });

  const tasksForPlaylist = isTemplate ? templateTasks : ownTasks;
  const isLoadingTasks = isTemplate ? isLoadingTemplateTasks : isLoadingOwnTasks;

  const sourceTasks = localTasks ?? tasksForPlaylist ?? [];
  const { sortedTasks, sortMenuItems, toggleSort } = usePlaylistTasksSort(sourceTasks);

  const listRef = useRef<HTMLDivElement | null>(null);

  // While dragging or after a manual reorder, render the local (unsorted) order.
  const visibleTasks = useMemo(() => {
    if (preferLocalOrder) return localTasks ?? tasksForPlaylist ?? [];

    return sortedTasks ?? [];
  }, [preferLocalOrder, localTasks, tasksForPlaylist, sortedTasks]);

  useEffect(() => {
    if (!tasksForPlaylist) {
      setLocalTasks(null);

      return;
    }

    setLocalTasks((current) => {
      if (preferLocalOrder || draggingFlag) {
        return mergeServerIntoLocalPreservingOrder(current ?? [], tasksForPlaylist);
      }

      return tasksForPlaylist;
    });
  }, [playlistId, tasksForPlaylist, preferLocalOrder, draggingFlag]);

  const dnd = usePlaylistDnd({
    visibleTasks,
    listRef,
    currentPlaylistId: playlistId,
    updateTaskOrder,
    setLocalTasks,
    sourceTasks,
    autoscroll: { edgeThresholdPx: 96, stepPx: 12 },
    isReorderMode,
  });

  useEffect(() => {
    setDraggingFlag(dnd.dragging);
    if (dnd.dragging) setPreferLocalOrder(true);
  }, [dnd.dragging]);

  const wrappedSortMenuItems = useMemo(
    () =>
      sortMenuItems.map((item) => ({
        ...item,
        onClick: () => {
          setPreferLocalOrder(false);
          item.onClick?.();
        },
      })),
    [sortMenuItems],
  );

  const handleToggleReorderMode = () => {
    setIsReorderMode(!isReorderMode);
    if (!isReorderMode) {
      const tasksSortedByOrder = [...(tasksForPlaylist ?? [])].sort((a, b) => {
        if (a.order === null && b.order === null) return 0;
        if (a.order === null) return 1;
        if (b.order === null) return -1;

        return a.order - b.order;
      });

      setLocalTasks(tasksSortedByOrder);
      setPreferLocalOrder(true);
    }
  };

  const onDragEnd: typeof dnd.onDragEnd = async (evt) => {
    await dnd.onDragEnd(evt);
    setPreferLocalOrder(true);
    toggleSort('order');
  };

  return {
    listRef,
    visibleTasks,
    isLoadingTasks,
    isReorderMode,
    wrappedSortMenuItems,
    handleToggleReorderMode,
    dnd: { ...dnd, onDragEnd },
  };
};

export type PlaylistTaskListState = ReturnType<typeof usePlaylistTaskList>;

type PlaylistTaskListProps = Readonly<{
  playlist: PriorityPlaylist;
  state: PlaylistTaskListState;
  isInSidebar?: boolean;
  /** `card` renders a task card; `row` renders a detail-header row. */
  variant?: 'card' | 'row';
  /** Drops every mutating affordance from the rows — for a playbook the viewer doesn't own. */
  readOnly?: boolean;
  onOpenTask?: (task: PriorityTask) => void;
  containerSx?: SxProps<Theme>;
  skeletonWidth?: number | string;
}>;

export const PlaylistTaskList = ({
  playlist,
  state,
  isInSidebar = false,
  variant = 'card',
  readOnly = false,
  onOpenTask,
  containerSx,
  skeletonWidth = PLAYLIST_CARD_DRAWER_WIDTH,
}: PlaylistTaskListProps) => {
  const { listRef, visibleTasks, isLoadingTasks, isReorderMode, dnd } = state;
  const { deleteTask, handleOpenEditTaskDialog, handleOpenNewTaskDialog, updateTask } =
    useGetTaskActionsAndProperties();

  const renderRow = (task: PriorityTask) => (
    <PlaybookTaskRow
      task={task}
      isReorderMode={isReorderMode}
      onOpen={onOpenTask}
      onToggleComplete={
        readOnly
          ? undefined
          : async (t) => {
              try {
                await updateTask({
                  id: t.id,
                  status_code: getToggledTaskStatusCode(t.status_code),
                }).unwrap();
              } catch (error) {
                console.error('Failed to update task:', error);
              }
            }
      }
      onDuplicate={readOnly ? undefined : (t) => handleOpenNewTaskDialog(playlist, false, t)}
      onDelete={
        readOnly
          ? undefined
          : async (t) => {
              await deleteTask(t.id).unwrap();
            }
      }
      onEdit={readOnly ? undefined : (t) => handleOpenEditTaskDialog(t, false)}
    />
  );

  const renderCard = (task: PriorityTask) => (
    <TaskCard
      task={task}
      restrictWidth={false}
      isInSidebar={isInSidebar}
      isDraggable={isReorderMode}
      deleteTask={deleteTask}
      openEditTaskDialog={handleOpenEditTaskDialog}
      handleDuplicateTask={(t) => handleOpenNewTaskDialog(playlist, true, t)}
      updateTask={async (updatedPartial) => {
        try {
          await updateTask({ ...updatedPartial, id: task.id }).unwrap();
        } catch (error) {
          console.error('Failed to update task:', error);
        }
      }}
      onOpen={isReorderMode ? undefined : onOpenTask}
      isHighlighted={isReorderMode}
    />
  );

  const renderItem = variant === 'row' ? renderRow : renderCard;

  return (
    <DndContext
      sensors={dnd.sensors}
      collisionDetection={closestCenter}
      onDragStart={dnd.onDragStart}
      onDragMove={dnd.onDragMove}
      onDragEnd={dnd.onDragEnd}
    >
      <SortableContext items={visibleTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <Box
          sx={[defaultContainerSx, ...(Array.isArray(containerSx) ? containerSx : [containerSx])]}
          ref={listRef}
        >
          {isLoadingTasks &&
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={PLAYLIST_SKELETON_HEIGHT}
                width={skeletonWidth}
                animation="pulse"
              />
            ))}

          {!isLoadingTasks &&
            visibleTasks.map((task) => (
              <SortableRow key={task.id} task={task} isReorderMode={isReorderMode}>
                {renderItem(task)}
              </SortableRow>
            ))}
        </Box>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {dnd.activeTask ? (
          <Box sx={{ width: skeletonWidth }}>{renderItem(dnd.activeTask)}</Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

/**
 * Sortable wrapper; hides the in-list original while the overlay is displayed.
 */
const SortableRow = ({
  task,
  children,
  isReorderMode,
}: {
  task: PriorityTask;
  children: React.ReactNode;
  isReorderMode: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !isReorderMode,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...(isReorderMode ? listeners : {})}
      sx={getSortableRowSx(transform, transition, isDragging, isReorderMode)}
    >
      {children}
    </Box>
  );
};

const defaultContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-xs'],
});

const getSortableRowSx = (
  transform: { x: number; y: number; scaleX: number; scaleY: number } | null | undefined,
  transition: string | undefined,
  isDragging: boolean,
  isReorderMode: boolean,
): SxProps<Theme> => ({
  cursor: isReorderMode ? 'grab' : 'default',
  transform: CSS.Transform.toString(transform || null),
  transition,
  position: 'relative',
  opacity: isDragging ? 0 : 1,
  pointerEvents: isDragging ? 'none' : 'auto',
  '&:active': { cursor: isReorderMode ? 'grabbing' : 'default' },
});
